import React, { useEffect, useState } from "react";
import { api, rupee } from "../lib/api";
import { TrendingUp, TrendingDown, Package, Users, Wallet, ScanLine, AlertTriangle } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Link } from "react-router-dom";

export default function Dashboard() {
  const [s, setS] = useState(null);
  const [low, setLow] = useState([]);
  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setS(r.data));
    api.get("/dashboard/low-stock").then((r) => setLow(r.data));
  }, []);
  if (!s) return <div className="p-8">Loading...</div>;

  const stats = [
    { label: "Today's revenue", value: rupee(s.today_revenue), icon: TrendingUp, color: "var(--success)", testid: "stat-today-revenue" },
    { label: "This month", value: rupee(s.month_revenue), icon: TrendingUp, color: "var(--primary)", testid: "stat-month-revenue" },
    { label: "This month expenses", value: rupee(s.month_expenses), icon: TrendingDown, color: "var(--warning)", testid: "stat-month-expenses" },
    { label: "Profit (month)", value: rupee(s.profit_month), icon: Wallet, color: "var(--secondary)", testid: "stat-profit" },
  ];
  const mini = [
    { label: "Products in shelf", v: s.in_shelf, total: s.total_products, icon: Package },
    { label: "Sold", v: s.sold, total: s.total_products, icon: ScanLine },
    { label: "Customers", v: s.total_customers, icon: Users },
    { label: "Staff", v: s.total_staff, icon: Users },
  ];

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto" data-testid="dashboard-root">
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display font-bold text-3xl md:text-4xl">Namaste 🙏</h1>
          <p className="mt-1" style={{ color: "var(--text-muted)" }}>Here's what's happening in your store today.</p>
        </div>
        <Link to="/app/pos" data-testid="dashboard-open-pos" className="hidden md:inline-flex items-center gap-2 px-5 py-3 rounded-md text-white tactile-btn" style={{ background: "var(--primary)" }}>
          <ScanLine size={18} /> Open POS
        </Link>
      </div>

      <div className="grid md:grid-cols-4 gap-4">
        {stats.map((st) => (
          <div key={st.label} className="card-soft p-5" data-testid={st.testid}>
            <div className="flex items-start justify-between">
              <div className="text-sm" style={{ color: "var(--text-muted)" }}>{st.label}</div>
              <st.icon size={18} style={{ color: st.color }} />
            </div>
            <div className="mt-3 font-display font-bold text-2xl num">{st.value}</div>
          </div>
        ))}
      </div>

      <div className="mt-6 grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 card-soft p-5">
          <div className="flex items-center justify-between">
            <div className="font-display font-semibold text-lg">Last 7 days revenue</div>
            <div className="text-sm num" style={{ color: "var(--text-muted)" }}>Total: {rupee(s.chart_last_7.reduce((a, b) => a + b.revenue, 0))}</div>
          </div>
          <div className="h-64 mt-3">
            <ResponsiveContainer>
              <LineChart data={s.chart_last_7}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EEE7DA" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => rupee(v)} />
                <Line type="monotone" dataKey="revenue" stroke="var(--primary)" strokeWidth={2.5} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          {mini.map((m) => (
            <div key={m.label} className="card-soft p-5">
              <div className="text-xs" style={{ color: "var(--text-muted)" }}>{m.label}</div>
              <div className="font-display font-bold text-2xl mt-2 num">{m.v}{m.total !== undefined && <span className="text-sm num font-normal" style={{ color: "var(--text-muted)" }}> / {m.total}</span>}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6 card-soft p-5" data-testid="dashboard-low-stock-card">
        <div className="flex items-center justify-between mb-3">
          <div className="font-display font-semibold text-lg flex items-center gap-2"><AlertTriangle size={18} style={{ color: "var(--warning)" }} /> Low stock alerts</div>
          <Link to="/app/products" className="text-sm font-semibold" style={{ color: "var(--primary)" }}>View all →</Link>
        </div>
        {low.length === 0 ? (
          <div className="text-sm py-6 text-center" style={{ color: "var(--text-muted)" }}>All good. Nothing running low.</div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {low.slice(0, 6).map((p) => (
              <div key={p.id} className="border rounded-md p-3 flex items-center justify-between" style={{ borderColor: "var(--border-soft)" }} data-testid={`dashboard-low-${p.barcode}`}>
                <div className="min-w-0">
                  <div className="font-medium truncate">{p.name}</div>
                  <div className="text-xs font-mono" style={{ color: "var(--text-muted)" }}>{p.barcode}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs" style={{ color: "var(--text-muted)" }}>Stock</div>
                  <div className="font-display font-bold num" style={{ color: "var(--warning)" }}>{p.stock}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-6 card-soft p-5">
        <div className="font-display font-semibold text-lg mb-3">Recent sales</div>
        {s.recent_sales.length === 0 ? (
          <div className="text-sm py-6 text-center" style={{ color: "var(--text-muted)" }}>No sales yet. <Link to="/app/pos" className="font-semibold" style={{ color: "var(--primary)" }}>Create your first bill</Link>.</div>
        ) : (
          <table className="w-full text-sm zebra">
            <thead><tr className="text-left" style={{ color: "var(--text-muted)" }}><th className="py-2">Invoice</th><th>Customer</th><th>Items</th><th className="text-right">Total</th></tr></thead>
            <tbody>
              {s.recent_sales.map((r) => (
                <tr key={r.id} className="border-t" style={{ borderColor: "var(--border-soft)" }}>
                  <td className="py-3 font-medium">{r.invoice_no}</td>
                  <td>{r.customer_snapshot?.name || "Walk-in"}</td>
                  <td>{r.items.length}</td>
                  <td className="text-right num font-semibold">{rupee(r.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
